import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useAcceptExpertiseOrderMutation, useGetExpertiseOrdersQuery } from '@/core/services/order'

import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

import { getApiErrorMessage } from '@/utils/errors'

import ExpertiseOrders from '../components/ExpertiseOrders'
import { useSelectNetwork } from '@/core/services/network'
import { isStaff } from '@/utils/type-guards'
import { OrderStatus } from '@/core/models/order.model'

export default function ExpertiseOrdersContainer() {
  const { networkId } = useParams()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const authUser = useAppSelector(selectAuthUser)

  const { data: network } = useSelectNetwork(networkId)

  const { data: rawExpertiseOrders } = useGetExpertiseOrdersQuery({ network: networkId }, { refetchOnMountOrArgChange: true, skip: !networkId })
  const [acceptExpertiseOrder, { isLoading: isAcceptingExpertiseOrder, originalArgs: acceptedExpertiseOrderId }] = useAcceptExpertiseOrderMutation()

  const isNetworkMember = useMemo(() => !!network?.members.find((member) => member === authUser?._id), [authUser, network])

  const expertiseOrders = useMemo(
    () =>
      rawExpertiseOrders?.map((expertiseOrder) => {
        const acceptedBy = expertiseOrder.acceptedBy
        const consultations = expertiseOrder.expertise.consultations
        return {
          id: expertiseOrder._id,
          acceptedBy: acceptedBy ? `${acceptedBy.userInfo.name} ${acceptedBy.userInfo.surname}` : undefined,
          canAccept: isNetworkMember && isStaff(authUser) && authUser.status === 'MEDECIN',
          canOpen: isNetworkMember && (expertiseOrder.status === OrderStatus.Accepted || expertiseOrder.createdBy._id === authUser?._id),
          createdBy: `${expertiseOrder.createdBy.userInfo.name} ${expertiseOrder.createdBy.userInfo.surname}`,
          consultationId: consultations.length ? (typeof consultations[0] === 'string' ? consultations[0] : consultations[0]._id) : undefined,
          expertiseId: expertiseOrder.expertise._id,
          isAccepting: isAcceptingExpertiseOrder && acceptedExpertiseOrderId === expertiseOrder._id,
          patient: `${expertiseOrder.patient.userInfo.name} ${expertiseOrder.patient.userInfo.surname}`,
          speciality: expertiseOrder.expertise.speciality,
          status: expertiseOrder.status,
        }
      }) ?? [],
    [acceptedExpertiseOrderId, authUser, isAcceptingExpertiseOrder, isNetworkMember, rawExpertiseOrders]
  )

  return (
    <ExpertiseOrders
      expertiseOrders={expertiseOrders}
      onAccept={async (expertiseOrderId) => {
        if (!authUser) return
        try {
          await acceptExpertiseOrder(expertiseOrderId).unwrap()
        } catch (error) {
          enqueueSnackbar(getApiErrorMessage(error, t('notification.expertiseOrder.accept.error')), { variant: 'error' })
        }
      }}
      readOnly={!isNetworkMember}
    />
  )
}
